import React, { useState, useEffect } from 'react';
import { authHelper } from '../utils/authHelper';

const API = process.env.REACT_APP_BACKEND_URL;

function AdminLMSCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    thumbnail_url: '',
    price: 2500
  });

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const res = await fetch(`${API}/api/admin/lms/courses`, {
        headers: authHelper.getAuthHeaders()
      });
      const data = await res.json();
      setCourses(data.courses || []);
    } catch (err) {
      console.error('Error loading courses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/admin/lms/courses`, {
        method: 'POST',
        headers: {
          ...authHelper.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        await loadCourses();
        setShowCreateModal(false);
        resetForm();
      }
    } catch (err) {
      console.error('Error creating course:', err);
    }
  };

  const handleUpdateCourse = async (courseId, updates) => {
    try {
      const res = await fetch(`${API}/api/admin/lms/courses/${courseId}`, {
        method: 'PUT',
        headers: {
          ...authHelper.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        await loadCourses();
      }
    } catch (err) {
      console.error('Error updating course:', err);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('¿Eliminar este curso? Se borrarán todos sus módulos y lecciones.')) return;
    
    try {
      const res = await fetch(`${API}/api/admin/lms/courses/${courseId}`, {
        method: 'DELETE',
        headers: authHelper.getAuthHeaders()
      });
      
      if (res.ok) {
        await loadCourses();
      }
    } catch (err) {
      console.error('Error deleting course:', err);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', thumbnail_url: '', price: 2500 });
    setEditingCourse(null);
  };

  if (loading) {
    return <div className="p-8 text-center">Cargando cursos...</div>;
  }

  return (
    <div className="p-6 lg:p-8 bg-[#02040A] min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Gestión de Cursos LMS</h1>
            <p className="text-gray-400">Crear y gestionar cursos de la Plataforma Web</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-[#C5A14C] text-white rounded-lg hover:bg-[#B89140] transition-colors"
          >
            + Crear Curso
          </button>
        </div>

        {/* Courses Grid */}
        {courses.length === 0 ? (
          <div className="bg-gray-800 rounded-lg p-12 text-center">
            <p className="text-gray-400 text-lg mb-4">No hay cursos creados todavía</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-[#C5A14C] text-white rounded-lg hover:bg-[#B89140] transition-colors"
            >
              Crear tu primer curso
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map(course => (
              <div key={course.course_id} className="bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
                {/* Thumbnail */}
                <div className="h-48 bg-gray-900 flex items-center justify-center">
                  {course.thumbnail_url ? (
                    <img src={course.thumbnail_url} alt={course.title} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-6xl">📚</span>
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-bold text-white">{course.title}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      course.status === 'active' ? 'bg-green-900 text-green-300' :
                      course.status === 'draft' ? 'bg-yellow-900 text-yellow-300' :
                      'bg-gray-700 text-gray-400'
                    }`}>
                      {course.status === 'active' ? 'Activo' : course.status === 'draft' ? 'Borrador' : 'Archivado'}
                    </span>
                  </div>

                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">{course.description}</p>

                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[#C5A14C] font-bold">${course.price}</span>
                    <span className="text-gray-500 text-sm">{course.module_count || 0} módulos</span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => window.location.href = `#/admin/lms/courses/${course.course_id}`}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                    >
                      Gestionar
                    </button>
                    {course.status === 'draft' && (
                      <button
                        onClick={() => handleUpdateCourse(course.course_id, { status: 'active' })}
                        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
                      >
                        Publicar
                      </button>
                    )}
                    {course.status === 'active' && (
                      <button
                        onClick={() => handleUpdateCourse(course.course_id, { status: 'draft' })}
                        className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors text-sm"
                      >
                        Ocultar
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteCourse(course.course_id)}
                      className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-lg max-w-2xl w-full p-6">
              <h2 className="text-2xl font-bold text-white mb-6">Crear Nuevo Curso</h2>
              
              <form onSubmit={handleCreateCourse}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-300 mb-2">Título del Curso *</label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-[#C5A14C] outline-none"
                      placeholder="Ej: Trading Avanzado - Método 11"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">Descripción *</label>
                    <textarea
                      required
                      rows="4"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-[#C5A14C] outline-none"
                      placeholder="Describe el contenido del curso..."
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">URL de Imagen (opcional)</label>
                    <input
                      type="text"
                      value={formData.thumbnail_url}
                      onChange={(e) => setFormData({...formData, thumbnail_url: e.target.value})}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-[#C5A14C] outline-none"
                      placeholder="/uploads/course-thumb.png"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">Precio (USD)</label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                      className="w-full px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-[#C5A14C] outline-none"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {setShowCreateModal(false); resetForm();}}
                    className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-[#C5A14C] text-white rounded-lg hover:bg-[#B89140] transition-colors"
                  >
                    Crear Curso
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminLMSCourses;
